import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileCode } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/core/components/ui/alert-dialog';
import { WeezmoGrid } from '../organisms/WeezmoGrid';
import { UploadWeezmoDialog } from '../organisms/UploadWeezmoDialog';
import { useWeezmoManagementStore } from '../../stores/weezmo-management-store';
import { MediaItem } from '../../types/sound-management-types';
import { toast } from 'sonner';

export function WeezmoManagement() {
  const navigate = useNavigate();

  const {
    files,
    totalCount,
    currentPage,
    pageSize,
    isLoading,
    isUploading,
    isDeleting,
    error,
    uploadError,
    uploadDialogOpen,
    deleteDialogOpen,
    fileToDelete,
    fetchFiles,
    uploadFile,
    deleteFile,
    setCurrentPage,
    openUploadDialog,
    closeUploadDialog,
    openDeleteDialog,
    closeDeleteDialog,
  } = useWeezmoManagementStore();

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles, currentPage]);

  const handlePageChange = useCallback(
    (page: number) => setCurrentPage(page),
    [setCurrentPage]
  );

  const handleUpload = useCallback(
    async (file: File) => {
      await uploadFile(file);
      toast.success(`"${file.name}" uploaded successfully`);
    },
    [uploadFile]
  );

  const handleDeleteClick = useCallback(
    (item: MediaItem) => openDeleteDialog(item),
    [openDeleteDialog]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!fileToDelete) return;
    const success = await deleteFile(fileToDelete.mediaId);
    if (success) {
      toast.success(`"${fileToDelete.originalFileName}" deleted successfully`);
    }
  }, [fileToDelete, deleteFile]);

  const handleRetry = useCallback(() => fetchFiles(), [fetchFiles]);

  return (
    <div className="flex flex-col h-full min-h-screen overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/translations')}
              className="h-8 w-8 p-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Weezmo Files (JS)</h1>
              <p className="text-muted-foreground">
                Manage JavaScript files for Weezmo Web — {totalCount} file{totalCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <Button onClick={openUploadDialog}>
            <Upload className="h-4 w-4 mr-2" />
            Upload File
          </Button>
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <FileCode className="h-4 w-4" />
          <span>Application: Weezmo Web · Media type: JS File</span>
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 min-h-0 overflow-auto px-6 pb-4">
        <WeezmoGrid
          files={files}
          isLoading={isLoading}
          error={error}
          totalCount={totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onDelete={handleDeleteClick}
          onRetry={handleRetry}
        />
      </div>

      {/* Upload Dialog */}
      <UploadWeezmoDialog
        open={uploadDialogOpen}
        onClose={closeUploadDialog}
        onUpload={handleUpload}
        isUploading={isUploading}
        error={uploadError}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => !open && closeDeleteDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete File</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <strong>"{fileToDelete?.originalFileName}"</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
